import { useRef, useState, useEffect, useCallback } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import moment from 'moment'

// material-ui
import { useTheme } from '@mui/material/styles'
import {
  Avatar,
  Badge,
  Box,
  ClickAwayListener,
  Divider,
  IconButton,
  List,
  Paper,
  Popper,
  Stack,
  Tooltip,
  Typography,
  useMediaQuery
} from '@mui/material'

// project import
import MainCard from 'components/MainCard'
import Transitions from 'components/@extended/Transitions'
import { getCookie } from 'helpers/cookies'
import { useSnackbar } from 'helpers/useSnackbar'
import { isNewArrival, playNotificationSound } from 'helpers/notificationAlert'
import { openPresignedDownload } from 'helpers/presignedDownload'
import { startVisiblePolling } from 'helpers/visiblePolling'
import { notificationActions as Actions } from 'store/notifications'
import { notificationView, toastNavLink } from './notificationConfig'

// assets
import CloseRoundedIcon from '@mui/icons-material/CloseRounded'
import BellOutlined from '@ant-design/icons/BellOutlined'
import CheckOutlined from '@ant-design/icons/CheckOutlined'
import DeleteOutlined from '@ant-design/icons/DeleteOutlined'

const avatarSX = { width: 36, height: 36 }

const POLL_INTERVAL = 30000

// ==============================|| HEADER CONTENT - NOTIFICATION ||============================== //

const Notification = () => {
  const theme = useTheme()
  const matchesXs = useMediaQuery(theme.breakpoints.down('md'))

  const dispatch = useDispatch()
  const navigate = useNavigate()

  const anchorRef = useRef(null)
  const [open, setOpen] = useState(false)

  const token = useCallback(() => getCookie(`${process.env.REACT_APP_WEBSITE_NAME}accessToken`), [])

  const fetchList = useCallback(() => {
    const accessToken = token()
    if (!accessToken) {
      return false
    }
    dispatch(Actions.notificationsList({ data: { limit: 10, offset: 0 }, token: accessToken, preserveState: true }))
    return true
  }, [dispatch, token])

  useEffect(() => startVisiblePolling(fetchList, POLL_INTERVAL), [fetchList])

  const listRes = useSelector((state) => state.notifications_data.notificationsList)
  const notifications = listRes?.data?.data ?? []
  const unreadCount = listRes?.data?.unreadCount ?? 0

  const { setSnackbar } = useSnackbar()
  const prevUnreadRef = useRef(null)
  const notificationsRef = useRef(notifications)
  notificationsRef.current = notifications
  const listStatus = listRes?.status
  useEffect(() => {
    // Before the first fetch resolves the store reports 0 unread, so recording that
    // baseline would chime the 0 -> N page-load jump as if they were new arrivals.
    if (listStatus !== 'success') {
      return
    }
    const prev = prevUnreadRef.current
    prevUnreadRef.current = unreadCount
    if (!isNewArrival(prev, unreadCount)) {
      return
    }
    const latest = notificationsRef.current[0]
    setSnackbar({
      message: latest?.title ? `New notification: ${latest.title}` : 'You have a new notification',
      alert: { color: 'info' },
      navLink: latest ? toastNavLink(latest) : undefined
    })
    playNotificationSound()
  }, [listStatus, unreadCount, setSnackbar])

  // The mutation ducks delay ~600ms before firing, so refetch on their SUCCESS
  // (not synchronously after dispatch) or the list/badge re-read stale state.
  const markReadRes = useSelector((s) => s.notifications_data.notificationMarkRead)
  const removeRes = useSelector((s) => s.notifications_data.notificationRemove)
  const markAllRes = useSelector((s) => s.notifications_data.notificationsMarkAllRead)
  useEffect(() => {
    if ([markReadRes?.status, removeRes?.status, markAllRes?.status].includes('success')) {
      fetchList()
    }
  }, [markReadRes?.status, removeRes?.status, markAllRes?.status, fetchList])

  const handleToggle = () => setOpen((prevOpen) => !prevOpen)
  const handleClose = (event) => {
    if (anchorRef.current && anchorRef.current.contains(event.target)) {
      return
    }
    setOpen(false)
  }

  const markRead = (item) => {
    if (item.read) {
      return
    }
    dispatch(Actions.notificationMarkRead({ id: item._id, token: token() }))
  }
  const removeItem = (item) => dispatch(Actions.notificationRemove({ id: item._id, token: token() }))
  const markAllRead = () => dispatch(Actions.notificationsMarkAllRead({ token: token() }))

  // Own call, not the shared invoices duck, so it never double-fires with a page's own
  // download handler. onFailure routes expired/deleted exports to a page with real error feedback.
  const downloadFromApi = (path, fallbackRoute) => openPresignedDownload({ path, token: token(), onFailure: () => navigate(fallbackRoute) })

  const runAction = (item) => {
    markRead(item)
    const { action } = notificationView(item)
    if (action.kind === 'navigate' && action.to) {
      navigate(action.to)
      setOpen(false)
    } else if (action.kind === 'download-combined') {
      downloadFromApi(`/invoices/combined/${action.id}/download`, '/billing/invoices')
    } else if (action.kind === 'download-export') {
      downloadFromApi(action.path, action.fallbackTo)
    }
  }

  const iconBackColorOpen = 'grey.300'
  const iconBackColor = 'grey.100'

  return (
    <Box sx={{ flexShrink: 0, ml: 0.75 }}>
      <IconButton
        disableRipple
        color="secondary"
        sx={{ color: 'text.primary', bgcolor: open ? iconBackColorOpen : iconBackColor }}
        aria-label="open notifications"
        ref={anchorRef}
        aria-controls={open ? 'profile-grow' : undefined}
        aria-haspopup="true"
        onClick={handleToggle}
      >
        <Badge badgeContent={unreadCount} color="primary" invisible={unreadCount === 0}>
          <BellOutlined style={{ fontSize: '20px' }} />
        </Badge>
      </IconButton>
      <Popper
        sx={{ zIndex: 100 }}
        placement={matchesXs ? 'bottom' : 'bottom-end'}
        open={open}
        anchorEl={anchorRef.current}
        role={undefined}
        transition
        disablePortal
        popperOptions={{ modifiers: [{ name: 'offset', options: { offset: [matchesXs ? -5 : 0, 9] } }] }}
      >
        {({ TransitionProps }) => (
          <Transitions type="fade" in={open} {...TransitionProps}>
            <Paper
              sx={{
                width: '100%',
                minWidth: 285,
                maxWidth: 420,
                [theme.breakpoints.down('md')]: { maxWidth: 285 }
              }}
            >
              <ClickAwayListener onClickAway={handleClose}>
                <MainCard
                  title="Notification"
                  elevation={0}
                  border={false}
                  content={false}
                  secondary={
                    <IconButton size="small" onClick={handleToggle}>
                      <CloseRoundedIcon />
                    </IconButton>
                  }
                >
                  <List component="nav" sx={{ p: 0 }}>
                    {notifications.length === 0 && (
                      <Box sx={{ textAlign: 'center', py: 1.5 }}>
                        <Typography variant="h6" color="textSecondary">
                          No notifications
                        </Typography>
                      </Box>
                    )}

                    {notifications.map((item, index) => {
                      const view = notificationView(item)
                      const { Icon } = view
                      return (
                        <Box key={item._id}>
                          {index > 0 && <Divider />}
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'flex-start',
                              gap: 1.25,
                              px: 2,
                              py: 1.25,
                              bgcolor: item.read ? 'transparent' : 'primary.lighter',
                              '&:hover': { bgcolor: 'action.hover' }
                            }}
                          >
                            <Box
                              onClick={() => runAction(item)}
                              sx={{ display: 'flex', flex: 1, gap: 1.25, cursor: 'pointer', minWidth: 0 }}
                            >
                              <Avatar sx={{ ...avatarSX, bgcolor: `${view.color}.main`, color: 'common.white' }}>
                                <Icon style={{ fontSize: 18 }} />
                              </Avatar>
                              <Box sx={{ minWidth: 0 }}>
                                <Typography variant="h6" sx={{ fontWeight: item.read ? 400 : 600 }}>
                                  {item.title}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                  {item.message}
                                </Typography>
                              </Box>
                            </Box>
                            <Stack alignItems="flex-end" spacing={0.5} sx={{ flexShrink: 0 }}>
                              <Typography variant="caption" color="text.secondary" noWrap>
                                {moment(item.createdAt).fromNow()}
                              </Typography>
                              <Stack direction="row" spacing={0.25}>
                                {!item.read && (
                                  <Tooltip title="Mark as read">
                                    <IconButton size="small" onClick={() => markRead(item)}>
                                      <CheckOutlined style={{ fontSize: 14 }} />
                                    </IconButton>
                                  </Tooltip>
                                )}
                                <Tooltip title="Remove">
                                  <IconButton size="small" onClick={() => removeItem(item)}>
                                    <DeleteOutlined style={{ fontSize: 14 }} />
                                  </IconButton>
                                </Tooltip>
                              </Stack>
                            </Stack>
                          </Box>
                        </Box>
                      )
                    })}

                    {notifications.length > 0 && (
                      <>
                        <Divider />
                        <Box
                          component="button"
                          onClick={markAllRead}
                          disabled={unreadCount === 0}
                          sx={{
                            width: '100%',
                            textAlign: 'center',
                            py: 1.5,
                            border: 0,
                            bgcolor: 'transparent',
                            cursor: unreadCount === 0 ? 'default' : 'pointer'
                          }}
                        >
                          <Typography variant="h6" color={unreadCount === 0 ? 'text.disabled' : 'primary'}>
                            Mark all as read
                          </Typography>
                        </Box>
                      </>
                    )}
                  </List>
                </MainCard>
              </ClickAwayListener>
            </Paper>
          </Transitions>
        )}
      </Popper>
    </Box>
  )
}

export default Notification
